/**
 * SDMS "Import Instrument File" dialog — OG's `ImportFiles` DashboardView (SpreadsheetEntryController
 * :9329, 500×500): an upload area (multi-select + drag-and-drop, 10 MB per file — :468-488) stages each
 * file into a list (`SDMSDCImport_ListView`: File Name, deletable). A name already staged is refused
 * with OG's "File already uploaded, select another file." (`Duplicatefile`). Nothing is imported until
 * **Import** (OG `Import_Execute` :1890): on success the dialog closes; on failure it stays open with the
 * staged files, as OG's "Data transfer failed." does.
 *
 * **Name** lists the template's DataTransfer rows (OG `DataTransfer_Sp`, first selected) and its pick DRIVES
 * the parse (owner decision 2026-09-28; OG stores the pick — `objDTName`, :1897 — but always parses with its
 * first row, `strDataTransfer` :9333 / :18757). Until the analyst picks one, staging only .txt files selects
 * the template's ".txt" row when it has one (MIB & Geosmin's _txt_DT) — the server's automatic choice.
 */
import { useEffect, useRef, useState, type DragEvent } from 'react';
import { AppDialog } from '../../components/AppDialog/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { useNotify } from '../../components/notifications';
import { ComboSelect } from '../../components/ComboSelect';

/** OG's upload-control cap (`ValidationSettings.MaxFileSize = 10485760`). */
const MAX_FILE_BYTES = 10_485_760;
const TOO_LARGE = 'File size exceeds the maximum allowed size, which is 10485760 bytes.';
const DUPLICATE = 'File already uploaded, select another file.';

interface StagedFile { id: string; fileName: string; size: string; file: File }

const COLUMNS: AppColumn<StagedFile>[] = [
  { field: 'fileName', header: 'File Name' },
  { field: 'size', header: 'Size', width: 90, align: 'right' },
];

const sizeText = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

export interface SdmsImportFilesDialogProps {
  open: boolean;
  /** `<input accept>` list, e.g. ".csv,.txt,.xls,.xlsx". */
  accept: string;
  /** The template's DataTransfer names (OG's "Name" dropdown). */
  transferNames: string[];
  onClose: () => void;
  /** Runs the import for the staged files, in staging order, with the chosen DataTransfer name (null when
   *  the template has none). Resolve true when it went through — the dialog then closes; false keeps it
   *  open with the files still staged. */
  onImport: (files: File[], dataTransferName: string | null) => Promise<boolean>;
}

export function SdmsImportFilesDialog({ open, accept, transferNames, onClose, onImport }: SdmsImportFilesDialogProps) {
  const notify = useNotify();
  const [staged, setStaged] = useState<StagedFile[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const [transferName, setTransferName] = useState('');
  const [namePicked, setNamePicked] = useState(false);   // the analyst chose a Name — stop defaulting it
  // OG selects the first name each time the dialog opens (SelectedIndex = 0).
  useEffect(() => { setTransferName(transferNames[0] ?? ''); setNamePicked(false); }, [transferNames, open]);
  // Default by file type until the analyst picks: only .txt files staged → the template's ".txt" row, if any.
  useEffect(() => {
    if (namePicked || !transferNames.length) return;
    const allTxt = staged.length > 0 && staged.every(s => /\.txt$/i.test(s.fileName));
    const txtRow = transferNames.find(n => /txt/i.test(n));
    setTransferName(allTxt && txtRow ? txtRow : transferNames[0]!);
  }, [staged, transferNames, namePicked]);

  const close = () => { if (busy) return; setStaged([]); setSelected(new Set()); onClose(); };

  const stage = (files: readonly File[]) => {
    const next = [...staged];
    for (const f of files) {
      if (f.size === 0) continue;
      if (f.size > MAX_FILE_BYTES) { notify.error(TOO_LARGE); continue; }
      if (next.some(s => s.fileName === f.name)) { notify.error(DUPLICATE); continue; }
      next.push({ id: `f${++seq.current}`, fileName: f.name, size: sizeText(f.size), file: f });
    }
    setStaged(next);
  };

  const removeSelected = () => {
    setStaged(staged.filter(s => !selected.has(s.id)));
    setSelected(new Set());
  };

  const runImport = async () => {
    if (!staged.length || busy) return;
    setBusy(true);
    try {
      const ok = await onImport(staged.map(s => s.file), transferName || null);
      if (ok) { setStaged([]); setSelected(new Set()); onClose(); }
    } finally {
      setBusy(false);
    }
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    stage(Array.from(e.dataTransfer.files ?? []));
  };

  return (
    <AppDialog open={open} title="Import Instrument File" icon="ti-file-import" width={500} onClose={close}
      closeOnBackdrop={false}
      footer={
        <>
          <button className="ca-btn" disabled={!selected.size || busy} onClick={removeSelected} title="Remove the selected files">
            <i className="ti ti-trash" /> Remove
          </button>
          <span style={{ flex: 1 }} />
          <button className="ca-btn" onClick={close} disabled={busy}>Cancel</button>
          <button className="ca-btn ca-btn-solid" disabled={!staged.length || busy} onClick={() => void runImport()}>
            <i className="ti ti-file-import" /> Import
          </button>
        </>
      }>
      <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', alignItems: 'center', gap: '8px 10px', marginBottom: 10 }}>
        <label>Name:</label>
        <ComboSelect value={transferName} options={transferNames} onChange={v => { setTransferName(v); setNamePicked(true); }} clearable={false}
          placeholder={transferNames.length ? 'Select…' : 'No data transfer template'} disabled={!transferNames.length} />
        <label style={{ alignSelf: 'start', paddingTop: 4 }}>Select File:</label>
      <div
        role="button"
        tabIndex={0}
        aria-label="Choose instrument files to import"
        onClick={() => inputRef.current?.click()}
        onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); inputRef.current?.click(); } }}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        style={{
          border: `1.5px dashed ${dragOver ? 'var(--ai-orange, #e8670a)' : 'var(--ai-line, #c8ccd4)'}`,
          borderRadius: 6, padding: '18px 12px', textAlign: 'center', cursor: 'pointer',
          background: dragOver ? 'var(--ai-row-hover, #f1f3f7)' : 'transparent', marginBottom: 10,
        }}>
        <i className="ti ti-cloud-upload" style={{ fontSize: 26, color: 'var(--ai-muted, #6b7280)' }} />
        <div>Drop the files here, or click to browse</div>
        <div style={{ color: 'var(--ai-muted, #6b7280)', fontSize: 12 }}>{accept.replace(/,/g, ', ')} · up to 10 MB each</div>
        <input ref={inputRef} type="file" multiple accept={accept} hidden data-unsaved-ignore
          onChange={e => { stage(Array.from(e.target.files ?? [])); e.target.value = ''; }} />
      </div>
      </div>
      <AppDataGrid<StagedFile>
        rows={staged}
        columns={COLUMNS}
        getRowId={r => r.id}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        hideHeaderTools
        emptyText="No files added yet."
      />
    </AppDialog>
  );
}
